import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatINR } from "../data/products";

const inputCls =
  "w-full border-b border-cocoa/25 bg-transparent py-3 text-sm font-light outline-none transition-colors placeholder:text-taupe/50 focus:border-gold";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-taupe";

const AuthForms = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back to Zelvoria");
      } else if (mode === "register") {
        await register(form.name, form.email, form.password);
        toast.success("Welcome to Zelvoria");
      } else {
        await api.post("/auth/forgot-password", { email: form.email });
        setForgotSent(true);
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const TABS = [
    { id: "login", label: "Sign In" },
    { id: "register", label: "Register" },
    { id: "forgot", label: "Forgot" },
  ];

  return (
    <div className="mx-auto max-w-md" data-testid="auth-forms">
      <div className="text-center">
        <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Your Account</p>
        <h1 className="mt-4 font-serif text-4xl font-light text-ink md:text-5xl">
          {mode === "login" ? "Welcome Back" : mode === "register" ? "Join Zelvoria" : "Reset Password"}
        </h1>
      </div>
      <div className="mt-8 flex justify-center gap-8 border-b border-beige">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => {
              setMode(t.id);
              setError("");
              setForgotSent(false);
            }}
            data-testid={`account-${t.id}-tab`}
            className={`relative pb-3 text-[11px] uppercase tracking-[0.25em] transition-colors ${
              mode === t.id ? "text-gold" : "text-taupe hover:text-ink"
            }`}
          >
            {t.label}
            {mode === t.id && <span className="absolute bottom-0 left-0 h-px w-full bg-gold" />}
          </button>
        ))}
      </div>

      {mode === "forgot" && forgotSent ? (
        <p className="py-12 text-center text-sm font-light text-taupe" data-testid="forgot-success">
          If an account exists for this email, a reset link has been sent.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-10 space-y-8" data-testid={`${mode}-form`}>
          {mode === "register" && (
            <div>
              <label htmlFor="auth-name" className={labelCls}>Full Name</label>
              <input id="auth-name" required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="register-name-input" className={inputCls} />
            </div>
          )}
          <div>
            <label htmlFor="auth-email" className={labelCls}>Email</label>
            <input id="auth-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid={`${mode}-email-input`} className={inputCls} />
          </div>
          {mode !== "forgot" && (
            <div>
              <label htmlFor="auth-password" className={labelCls}>Password</label>
              <input id="auth-password" type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder="••••••••" data-testid={`${mode}-password-input`} className={inputCls} />
            </div>
          )}
          {error && (
            <p className="text-sm text-red-800" data-testid="auth-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid={`${mode}-submit-button`}
            className="w-full bg-cocoa py-4 text-[11px] uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:bg-gold disabled:opacity-60"
          >
            {busy ? "One moment…" : mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Send Reset Link"}
          </button>
        </form>
      )}
    </div>
  );
};

const Orders = () => {
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    api
      .get("/orders/mine")
      .then((r) => setOrders(r.data))
      .catch(() => setOrders([]));
  }, []);

  if (orders === null) {
    return <p className="py-10 text-center font-serif text-xl italic text-taupe">Gathering your orders…</p>;
  }

  if (orders.length === 0) {
    return (
      <div className="border border-beige bg-parchment py-16 text-center" data-testid="orders-empty">
        <p className="font-serif text-2xl font-light italic text-ink">No orders yet — your first flame awaits.</p>
        <Link
          to="/candles"
          data-testid="orders-shop-button"
          className="mt-6 inline-block bg-cocoa px-8 py-3 text-[11px] uppercase tracking-[0.25em] text-ivory transition-colors hover:bg-gold"
        >
          Shop Candles
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="orders-list">
      {orders.map((o) => (
        <div key={o.id} className="border border-beige bg-parchment p-6 md:p-8" data-testid={`order-${o.order_number}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-beige pb-4">
            <div>
              <p className="font-serif text-xl text-ink">{o.order_number}</p>
              <p className="mt-1 text-xs text-taupe">
                {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>
            <span className="border border-gold/40 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-gold">
              {o.status}
            </span>
          </div>
          <div className="mt-4 space-y-2">
            {o.items.map((i) => (
              <div key={i.slug} className="flex justify-between text-sm">
                <span className="font-light text-ink">
                  {i.name} <span className="text-taupe">× {i.qty}</span>
                </span>
                <span className="text-ink">{formatINR(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-between border-t border-beige pt-4 text-sm">
            <span className="uppercase tracking-[0.15em] text-taupe">Total</span>
            <span className="font-medium text-ink">{formatINR(o.total)}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const Dashboard = ({ user, logout }) => {
  const [tab, setTab] = useState("orders");
  return (
    <div data-testid="account-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Your Account</p>
          <h1 className="mt-3 font-serif text-4xl font-light text-ink md:text-5xl">
            Welcome, {user.name.split(" ")[0]}.
          </h1>
        </div>
        <button
          onClick={logout}
          data-testid="logout-button"
          className="border border-cocoa/30 px-6 py-3 text-[10px] uppercase tracking-[0.25em] text-cocoa transition-colors hover:border-gold hover:text-gold"
        >
          Sign Out
        </button>
      </div>

      <div className="mt-10 flex gap-8 border-b border-beige">
        {[
          { id: "orders", label: "Orders" },
          { id: "profile", label: "Profile" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`dashboard-${t.id}-tab`}
            className={`relative pb-3 text-[11px] uppercase tracking-[0.25em] transition-colors ${
              tab === t.id ? "text-gold" : "text-taupe hover:text-ink"
            }`}
          >
            {t.label}
            {tab === t.id && <span className="absolute bottom-0 left-0 h-px w-full bg-gold" />}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "orders" ? (
          <Orders />
        ) : (
          <div className="max-w-md border border-beige bg-parchment p-8" data-testid="profile-card">
            <p className={labelCls}>Name</p>
            <p className="mt-2 font-serif text-2xl text-ink" data-testid="profile-name">{user.name}</p>
            <p className={`${labelCls} mt-8`}>Email</p>
            <p className="mt-2 text-sm text-ink" data-testid="profile-email">{user.email}</p>
            <p className={`${labelCls} mt-8`}>Member Since</p>
            <p className="mt-2 text-sm text-ink">
              {new Date(user.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default function Account() {
  const { user, logout } = useAuth();

  if (user === undefined) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center bg-ivory">
        <p className="font-serif text-2xl font-light italic text-taupe">One moment…</p>
      </div>
    );
  }

  return (
    <div className="bg-ivory" data-testid="account-page">
      <Seo title="Your Account — Zelvoria" siteName="ZELVORIA" description="Sign in to your Zelvoria account." />
      <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24">
        <Reveal>{user ? <Dashboard user={user} logout={logout} /> : <AuthForms />}</Reveal>
      </div>
    </div>
  );
}
